import React from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowUpRight } from "lucide-react";
import { SERVICES, IMAGES, ENGAGEMENT_LEVELS, DELIVERY_MODELS } from "@/data/site";
import { Reveal, Stagger, StaggerItem } from "@/components/Motion";
import { PageHero, CTABand, SectionHeading } from "@/components/UI";
import DeliveryBadges from "@/components/DeliveryBadges";

const Icon = ({ name, ...p }) => { const C = Icons[name] || Icons.Circle; return <C {...p} />; };

export default function Services() {
  return (
    <>
      <PageHero
        eyebrow="Our Services"
        title={["A consulting &", "fractional services firm"]}
        subtitle="We provide organizations with strategic workforce, operational, administrative, technology, marketing, training, security, and business support solutions, delivered as consulting, fractional, workforce, or a hybrid of the three."
        image={IMAGES.executiveMeeting}
      />

      {/* Four delivery models, how we engage */}
      <section id="delivery-models" className="bg-lightgray py-24 md:py-32 scroll-mt-24" data-testid="services-delivery-models">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <SectionHeading eyebrow="How We Deliver" title="Four flexible ways to put our expertise to work" intro="Every practice below can be delivered as consulting, fractional, workforce, or a hybrid mix, often blended inside a single engagement." />
          <Stagger className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
            {DELIVERY_MODELS.map((m) => (
              <StaggerItem key={m.slug}>
                <div data-testid={`services-model-${m.slug}`} className="bg-white p-8 md:p-10 h-full flex flex-col">
                  <div className="flex items-center justify-between">
                    <div className="w-12 h-12 border border-slate-200 flex items-center justify-center">
                      <Icon name={m.icon} className="w-5 h-5 text-navy" strokeWidth={1.5} />
                    </div>
                    <span className="font-display font-800 text-3xl text-slate-100">{m.id}</span>
                  </div>
                  <h3 className="mt-5 font-display font-700 text-xl text-navy leading-tight">{m.title}</h3>
                  <p className="mt-1 text-gold text-xs font-600 uppercase tracking-wider">{m.tagline}</p>
                  <p className="mt-3 text-gray-600 text-sm leading-relaxed">{m.short}</p>
                  <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-navy/75">
                    {m.includes.map((it) => (
                      <li key={it} className="flex items-start gap-2"><span className="mt-1.5 w-1 h-1 bg-gold shrink-0" /><span>{it}</span></li>
                    ))}
                  </ul>
                  {m.audience && <span className="mt-5 inline-flex self-start text-[10px] font-700 uppercase tracking-[0.16em] text-gold border border-gold px-2 py-0.5">{m.audience}</span>}
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="bg-white py-24 md:py-32" data-testid="services-overview">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <SectionHeading eyebrow="Practice Areas" title="The disciplines we bring to every engagement" intro="Each practice is led by senior practitioners and tailored to your organization. Explore a practice to see the full range of capabilities." />
          <Stagger className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => (
              <StaggerItem key={s.slug}>
                <Link to={`/services/${s.slug}`} data-testid={`services-card-${s.slug}`} className="group block bg-white border border-slate-200 p-8 h-full hover:border-navy hover:shadow-[0_20px_60px_rgba(15,45,82,0.1)] transition-all duration-400">
                  <div className="flex items-start justify-between">
                    <div className="w-14 h-14 bg-navy flex items-center justify-center group-hover:bg-gold transition-colors">
                      <Icon name={s.icon} className="w-6 h-6 text-white" strokeWidth={1.5} />
                    </div>
                    <ArrowUpRight className="w-5 h-5 text-slate-300 group-hover:text-gold group-hover:-translate-y-1 group-hover:translate-x-1 transition-all" />
                  </div>
                  <h3 className="mt-6 font-display font-700 text-lg text-navy leading-snug">{s.title}</h3>
                  <p className="mt-3 text-gray-600 text-sm leading-relaxed">{s.short}</p>
                  <div className="mt-6 flex flex-wrap gap-2">
                    {s.capabilities.slice(0, 3).map((c) => (
                      <span key={c} className="text-xs text-navy/70 bg-lightgray px-3 py-1 border border-slate-200">{c}</span>
                    ))}
                    <span className="text-xs text-gold px-1 py-1">+{s.capabilities.length - 3} more</span>
                  </div>
                  {s.delivery_models?.length > 0 && (
                    <div className="mt-5 pt-4 border-t border-slate-100">
                      <div className="text-[9px] font-700 uppercase tracking-[0.16em] text-navy/40 mb-2">Delivered as</div>
                      <DeliveryBadges
                        slugs={s.delivery_models}
                        variant="inline"
                        linkable={false}
                        testid={`services-card-${s.slug}-models`}
                      />
                    </div>
                  )}
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="bg-navy py-24 md:py-32 relative overflow-hidden" data-testid="engagement-levels">
        <div className="grain absolute inset-0 opacity-30" />
        <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8">
          <div className="max-w-2xl">
            <span className="overline text-gold text-[10px]">One Firm · Two Ways to Work</span>
            <h2 className="mt-4 font-display font-800 text-white text-4xl sm:text-5xl lg:text-6xl tracking-tightest leading-[1.02]">
              Scale the same package to the way you actually work.
            </h2>
            <p className="mt-6 text-white/70 font-body text-base leading-relaxed">
              Every Consilium Pro practice can be delivered at four levels of depth. Choose the mix that fits your capacity, your timeline, and your appetite for internal ownership.
            </p>
          </div>
          <Stagger className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {ENGAGEMENT_LEVELS.map((lvl, i) => (
              <StaggerItem key={lvl.name}>
                <div data-testid={`engagement-${lvl.name.toLowerCase().replace(/ /g, "-")}`} className="bg-white/[0.04] border border-white/10 p-8 h-full hover:bg-white/[0.06] hover:border-gold/50 transition-all duration-400">
                  <div className="w-12 h-12 bg-gold flex items-center justify-center">
                    <Icon name={lvl.icon} className="w-5 h-5 text-navy" strokeWidth={1.75} />
                  </div>
                  <div className="mt-6 flex items-baseline gap-3">
                    <span className="font-display font-800 text-gold text-2xl">{String(i + 1).padStart(2, "0")}</span>
                    <h3 className="font-display font-700 text-white text-lg tracking-tight">{lvl.name}</h3>
                  </div>
                  <p className="mt-4 text-white/70 text-sm leading-relaxed">{lvl.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <CTABand />
    </>
  );
}
